import { useState, useRef, useCallback, useEffect } from 'react';
import { CameraView, CameraViewHandle, TorchButton, type TorchState } from '@/components/CameraView';
import { CameraTopBar } from '@/components/CameraTopBar';
import { PhotoHeroResult } from '@/components/PhotoHeroResult';
import { PageHeader } from '@/components/PageHeader';
import { PracticeSettingsDrawer } from '@/components/PracticeSettingsDrawer';
import { FrogWaitAnimation } from '@/components/FrogWaitAnimation';
import { FloatingCaptureButton } from '@/components/registration';
import { IosVideoWarning } from '@/components/IosVideoWarning';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { cn } from '@/lib/utils';
import {
  recognizeDice,
  recognizeAuto,
  RecognitionError,
  DICE_TYPES
} from '@/services/recognition';
import { extractFirstFrame } from '@/services/videoFrameExtractor';
import { useVideoRollCapture } from '@/hooks/useVideoRollCapture';
import type { RollVideoRecording } from '@/services/rollVideoRecorder';
import { useSoulBondsStore } from '@/stores/soulBondsStore';

const SERVICE_TAG = '[PRACTICE]';

type DiceType = (typeof DICE_TYPES)[number]['value'];

interface CaptureResult {
  value: number;
  confidence: number;
  imageData: string;
  diceType: string;
  wasEdited: boolean;
  /** Capture mode (TICKET-281h). 'video' result includes videoBlob for inline playback. */
  kind: 'image' | 'video';
  /** Recorded clip blob for video captures (object URL constructed by PhotoHeroResult). */
  videoBlob?: Blob;
}

interface ErrorState {
  message: string;
  details: string;
  code: string;
}

export function PracticePage() {
  const isLandscape = useIsLandscape();
  const { appSettings, updateSettings } = useSoulBondsStore();
  const defaultDice = (appSettings.defaultDiceType as DiceType) || 'd20';
  const savedAutoDetect = appSettings.useAutoDetect ?? true;
  // TICKET-299: persistent capture-mode preference (photo vs video).
  const captureMode = appSettings.captureMode ?? 'photo';
  // Backend silently downgrades gated models for guests, so no client-side gating is needed.
  const aiModel = appSettings.aiRecognitionModel ?? 'gpt-4.1-mini';

  const [useAutoDetect, setUseAutoDetect] = useState(savedAutoDetect);
  const [selectedDie, setSelectedDie] = useState<DiceType>(defaultDice);
  const [isCapturing, setIsCapturing] = useState(false);
  const [result, setResult] = useState<CaptureResult | null>(null);
  const [error, setError] = useState<ErrorState | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [torchState, setTorchState] = useState<TorchState>({ supported: false, enabled: false });
  const cameraRef = useRef<CameraViewHandle>(null);

  // TICKET-290: motion-driven video capture.
  const videoCapture = useVideoRollCapture();

  // TICKET-126: Swipe gesture support for drawer handle
  const swipeDirection = isLandscape ? 'left' : 'up';
  const swipeHandlers = useSwipeGesture({
    direction: swipeDirection,
    threshold: 50,
    maxDuration: 500,
    onSwipe: () => {
      console.info(`${SERVICE_TAG} Drawer opened via swipe gesture`, {
        direction: swipeDirection
      });
      setDrawerOpen(true);
    }
  });

  // Persist dice selection to app settings
  useEffect(() => {
    if (selectedDie !== appSettings.defaultDiceType) {
      updateSettings({ defaultDiceType: selectedDie });
    }
  }, [selectedDie, appSettings.defaultDiceType, updateSettings]);

  // Persist auto-detect preference
  useEffect(() => {
    if (useAutoDetect !== appSettings.useAutoDetect) {
      updateSettings({ useAutoDetect });
    }
  }, [useAutoDetect, appSettings.useAutoDetect, updateSettings]);

  const handleCapture = useCallback(async () => {
    if (!cameraRef.current || isCapturing) return;

    setIsCapturing(true);
    setError(null);

    try {
      const imageData = cameraRef.current.capture();
      if (!imageData) {
        console.error(`${SERVICE_TAG} Failed to capture image`);
        setError({
          message: 'Failed to capture image',
          details: 'Camera capture returned no data',
          code: 'CAPTURE_FAILED'
        });
        setIsCapturing(false);
        return;
      }

      let recognition;
      let detectedType: string;

      if (useAutoDetect) {
        console.info(`${SERVICE_TAG} Using auto-detect mode`);
        recognition = await recognizeAuto(imageData, { model: aiModel });
        detectedType = recognition.diceType ?? 'unknown';
      } else {
        console.info(`${SERVICE_TAG} Using manual mode with ${selectedDie}`);
        recognition = await recognizeDice(imageData, selectedDie, { model: aiModel });
        detectedType = selectedDie;
      }

      setResult({
        value: recognition.value,
        confidence: recognition.confidence,
        imageData,
        diceType: detectedType,
        wasEdited: false,
        kind: 'image'
      });
    } catch (err) {
      console.error(`${SERVICE_TAG} Recognition failed:`, err);

      if (err instanceof RecognitionError) {
        setError({
          message: err.message,
          details: err.getDetailedMessage(),
          code: err.code
        });
      } else {
        setError({
          message: 'Recognition failed',
          details: err instanceof Error ? err.message : 'Unknown error',
          code: 'UNKNOWN'
        });
      }
    } finally {
      setIsCapturing(false);
    }
  }, [selectedDie, isCapturing, useAutoDetect, aiModel]);

  /**
   * Begin a motion-driven video roll capture (TICKET-290, long-press gesture).
   * The hook owns the recorder + motion detector; this just kicks it off.
   */
  const handleStartVideoCapture = useCallback(() => {
    if (!cameraRef.current || isCapturing) return;
    const stream = cameraRef.current.getMediaStream();
    const videoEl = cameraRef.current.getVideoElement();
    if (!stream || !videoEl) {
      setError({
        message: 'Camera not ready',
        details: 'No media stream available for video capture',
        code: 'CAMERA_NOT_READY'
      });
      return;
    }
    setError(null);
    setIsCapturing(true);
    videoCapture.start(stream, videoEl);
  }, [isCapturing, videoCapture]);

  // Run recognition once the hook delivers a finished recording.
  // Gate by recording identity via a ref so we never process the same blob
  // twice (the hook's return object identity changes each render, which would
  // otherwise re-fire this effect and produce a recognition-API loop on error).
  const processedRecordingRef = useRef<RollVideoRecording | null>(null);
  const useAutoDetectRef = useRef(useAutoDetect);
  useAutoDetectRef.current = useAutoDetect;
  const selectedDieRef = useRef(selectedDie);
  selectedDieRef.current = selectedDie;
  const aiModelRef = useRef(aiModel);
  aiModelRef.current = aiModel;
  useEffect(() => {
    if (videoCapture.state !== 'done' || !videoCapture.recording) return;
    if (processedRecordingRef.current === videoCapture.recording) return;
    processedRecordingRef.current = videoCapture.recording;

    const recordingBlob = videoCapture.recording.blob;
    const preFinalizedFrame = videoCapture.recording.preFinalizedFrame;
    let cancelled = false;

    (async () => {
      try {
        // Prefer the live still grabbed at settle (TICKET-293) — sharper than
        // re-decoding the recorded blob. Falls through to extractFirstFrame on
        // hard-cap (no settle) or capture failure.
        const firstFrame = preFinalizedFrame ?? await extractFirstFrame(recordingBlob);
        if (cancelled) return;

        let recognition;
        let detectedType: string;
        if (useAutoDetectRef.current) {
          recognition = await recognizeAuto(firstFrame, { model: aiModelRef.current });
          detectedType = recognition.diceType ?? 'unknown';
        } else {
          recognition = await recognizeDice(firstFrame, selectedDieRef.current, { model: aiModelRef.current });
          detectedType = selectedDieRef.current;
        }
        if (cancelled) return;

        setResult({
          value: recognition.value,
          confidence: recognition.confidence,
          imageData: firstFrame,
          diceType: detectedType,
          wasEdited: false,
          kind: 'video',
          videoBlob: recordingBlob
        });
      } catch (err) {
        if (cancelled) return;
        console.error(`${SERVICE_TAG} Video recognition failed:`, err);
        if (err instanceof RecognitionError) {
          setError({ message: err.message, details: err.getDetailedMessage(), code: err.code });
        } else {
          setError({
            message: 'Recognition failed',
            details: err instanceof Error ? err.message : 'Unknown error',
            code: 'UNKNOWN'
          });
        }
      } finally {
        if (!cancelled) {
          setIsCapturing(false);
          videoCapture.reset();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // videoCapture.reset is stable; other reads use refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoCapture.state, videoCapture.recording]);

  // Surface hook errors to the page error banner.
  useEffect(() => {
    if (videoCapture.state === 'error') {
      setError({
        message: 'Recording failed',
        details: videoCapture.errorMessage ?? 'Unknown error',
        code: videoCapture.errorCode ?? 'RECORDING_FAILED'
      });
      setIsCapturing(false);
      videoCapture.reset();
    }
  }, [videoCapture.state, videoCapture.errorCode, videoCapture.errorMessage, videoCapture]);

  // Reset the capturing flag when timeout fires (UI shows retry banner).
  useEffect(() => {
    if (videoCapture.state === 'timeout-no-motion') {
      setIsCapturing(false);
    }
  }, [videoCapture.state]);

  const handleTryAgain = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  const handleValueEdit = useCallback((newValue: number) => {
    if (result) {
      console.info(`${SERVICE_TAG} Value edited`, {
        oldValue: result.value,
        newValue,
        diceType: result.diceType
      });
      setResult({
        ...result,
        value: newValue,
        wasEdited: true
      });
    }
  }, [result]);

  const toggleAutoDetect = useCallback(() => {
    setUseAutoDetect((prev) => !prev);
  }, []);

  // Show error view
  if (error) {
    return (
      <div className={cn(
        'flex h-full p-4',
        isLandscape ? 'flex-row items-center gap-4' : 'flex-col'
      )}>
        <div className={cn(
          isLandscape ? 'flex-1 overflow-auto' : 'flex-1'
        )}>
          <PageHeader variant="test" title="Recognition Failed" splash={false} />
          <Card variant="elevated" className="border-destructive/50">
            <CardContent className={cn(isLandscape ? 'p-4' : 'p-6')}>
              <h2 className={cn('font-semibold mb-2', isLandscape ? 'text-lg' : 'text-xl')}>
                {error.message}
              </h2>
              <p className="mt-4 text-sm text-muted-foreground">{error.details}</p>
              <p className="mt-1 text-xs text-muted-foreground opacity-60">{error.code}</p>
            </CardContent>
          </Card>
        </div>

        <div className={cn(isLandscape ? 'w-48 flex-shrink-0' : 'pt-4')}>
          <Button
            variant="accent"
            size={isLandscape ? 'default' : 'lg'}
            className={cn('w-full', !isLandscape && 'py-6')}
            onClick={handleTryAgain}
            enablePressEffect
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Show result view after capture
  if (result) {
    return (
      <PhotoHeroResult
        imageData={result.imageData}
        kind={result.kind}
        videoBlob={result.videoBlob}
        value={result.value}
        diceType={result.diceType.toUpperCase()}
        confidence={result.confidence}
        animationState="entering"
        onValueEdit={handleValueEdit}
        wasEdited={result.wasEdited}
        actions={
          <div className="flex gap-3">
            <Button
              variant="secondary-action"
              size="lg"
              className="flex-1 py-5"
              onClick={handleTryAgain}
              enablePressEffect
            >
              Try Again
            </Button>
            <Button
              variant="accent"
              size="lg"
              className="flex-1 py-5"
              onClick={() => setResult(null)}
              enablePressEffect
            >
              New Capture
            </Button>
          </div>
        }
      />
    );
  }

  // TICKET-126: Camera-first layout with floating capture button
  return (
    <div className="relative h-full w-full">
      {/* Camera takes full container */}
      <div className="absolute inset-0 rounded-lg overflow-hidden m-2">
        <CameraView
          ref={cameraRef}
          className="h-full w-full"
          onTorchStateChange={setTorchState}
        />
      </div>

      {/* Unified top bar for camera controls */}
      <CameraTopBar
        left={
          torchState.supported ? (
            <TorchButton
              enabled={torchState.enabled}
              onToggle={() => cameraRef.current?.toggleTorch()}
            />
          ) : null
        }
        center={
          <div className={cn(
            'px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm',
            useAutoDetect
              ? 'bg-primary/80 text-primary-foreground'
              : 'bg-secondary/80 text-foreground'
          )}>
            {useAutoDetect ? 'Auto-Detect' : selectedDie.toUpperCase()}
          </div>
        }
        centerSecondary={
          videoCapture.state === 'waiting-for-motion' ? (
            <div className="rounded-full bg-red-600/80 px-4 py-2 text-white font-mono text-sm">
              <span className="inline-block h-2 w-2 mr-2 rounded-full bg-white animate-pulse" />
              Roll now! {videoCapture.secondsRemaining}s
            </div>
          ) : videoCapture.state === 'rolling' ? (
            <div className="rounded-full bg-red-600/80 px-4 py-2 text-white font-mono text-sm animate-pulse">
              <span className="inline-block h-2 w-2 mr-2 rounded-full bg-white" />
              Rolling…
            </div>
          ) : videoCapture.state === 'finalizing' ? (
            <div className="rounded-full bg-card/80 px-4 py-2 text-foreground text-sm">
              Processing…
            </div>
          ) : isCapturing ? <FrogWaitAnimation /> : null
        }
      />

      {/* TICKET-290: timeout retry banner (no recognition) */}
      {videoCapture.state === 'timeout-no-motion' && (
        <div className="absolute top-28 left-2 right-2 z-30">
          <Card variant="elevated" className="border-destructive/50">
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <span className="text-sm">No roll detected — try again</span>
              <Button
                size="sm"
                variant="accent"
                onClick={() => {
                  videoCapture.reset();
                  handleStartVideoCapture();
                }}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TICKET-281h: iOS warning — video capture may not work reliably on Safari */}
      <div className="absolute top-16 left-2 right-2 z-20 pointer-events-auto">
        <IosVideoWarning proofMode="either" />
      </div>

      {/* Floating capture button - positioned above nav bar */}
      <div className={cn(
        'absolute left-1/2 -translate-x-1/2 z-10',
        isLandscape ? 'bottom-4' : 'bottom-16'
      )}>
        <FloatingCaptureButton
          mode="manual"
          autoState="paused"
          // TICKET-299: tap follows the persistent capture-mode preference.
          // Long-press still toggles to video as a power-user shortcut.
          captureKind={captureMode}
          onCapture={captureMode === 'video' ? handleStartVideoCapture : handleCapture}
          onEnterAutoMode={() => {
            // TICKET-290: long-press → start motion-driven video recording.
            // Settings drawer remains accessible via the bottom handle button + swipe.
            console.info(`${SERVICE_TAG} Long press detected, starting video capture`);
            handleStartVideoCapture();
          }}
          onExitAutoMode={() => {}}
          onPause={() => {}}
          onResume={() => {}}
          disabled={isCapturing || videoCapture.state !== 'idle'}
        />
      </div>

      {/* Settings drawer */}
      <PracticeSettingsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        useAutoDetect={useAutoDetect}
        onToggleAutoDetect={toggleAutoDetect}
        selectedDie={selectedDie}
        onSelectDie={setSelectedDie}
        captureMode={captureMode}
        onCaptureModeChange={(mode) => updateSettings({ captureMode: mode })}
      />

      {/* Drawer handle trigger - click or swipe to open */}
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        {...swipeHandlers}
        className={cn(
          'absolute z-10 flex items-center justify-center',
          'bg-card/80 backdrop-blur-sm border border-primary/30 rounded-t-lg',
          'transition-colors hover:bg-card',
          isLandscape
            ? 'right-0 top-1/2 -translate-y-1/2 w-6 h-16 rounded-l-lg rounded-tr-none'
            : 'bottom-0 left-1/2 -translate-x-1/2 w-32 h-6'
        )}
        aria-label="Open settings. Click or swipe to open."
        data-testid="practice-drawer-handle"
      >
        <div className={cn(
          'bg-muted rounded-full',
          isLandscape ? 'w-1 h-8' : 'w-12 h-1'
        )} />
      </button>
    </div>
  );
}
